import type { i18n as I18nInstance } from 'i18next';
import type { BasketTableAnswerItem, EducationContent, QuestionConfigQuestionType } from '/index';

import useOnSitePowerGenerationSchema from '/business/shared/components/questions/scope1/_hooks/useOnSitePowerGenerationSchema';
import { basketTableTotalTypes, questionScopeCategories, questionSelectorOptions } from '/business/shared/util/Statics';
import formatNumber from '/shared/functions/formatNumber';

import gasCannister from '/business/airbp/images/icons/gasCannister.svg';
import { initiativeVisuals } from '/business/shared/components/questions/helper/initiativeVisuals';
import initiativeBackground from '/business/shared/images/initiatives/on-site-power-banner-1x.png';
import initiativeCardBackground from '/business/shared/images/initiatives/on-site-power-card-1x.png';

import useFormatSharedCustomScope1 from '/business/shared/components/pages/question/formats/customDataFormats/useFormatSharedCustomScope1';
import useFormatSharedSupplierScope1 from '/business/shared/components/pages/question/formats/supplierDataFormats/useFormatSharedSupplierScope1';
import educationContentJson from '/business/shared/components/questions/scope1/_education/power-generation.json';
import ReportingPeriod from '/business/shared/components/widgets/reporting-period/ReportingPeriod';
import StyledInfoFlash from '/shared/styled/StyledInfoFlash';
import StyledParagraph from '/shared/styled/components/StyledParagraph';

interface ConfigureOnSitePowerGenerationOptions {
  i18n: I18nInstance;
  reductionCardsSlides?: string[];
}

type FuelQuantityRow = {
  fuelUnit?: string;
  harmonisedInjection: (fuelUnit: string) => string;
  quantity: number;
};

type FuelTonnageRow = {
  tonnage: number;
};

function isEducationContent(value: object): value is EducationContent {
  return (
    typeof value === 'object' &&
    value !== null &&
    'topParas' in value &&
    Array.isArray(value.topParas) &&
    'exampleParas' in value &&
    Array.isArray(value.exampleParas)
  );
}

function getEducationContent(): EducationContent {
  if (!isEducationContent(educationContentJson)) {
    throw new Error('On-site power generation education content is not configured');
  }
  return educationContentJson;
}

export const getConfigureOnSitePowerGeneration = ({
  i18n,
  reductionCardsSlides = ['slide1'],
}: ConfigureOnSitePowerGenerationOptions): QuestionConfigQuestionType => {
  return {
    id: 'on-site-power-generation',
    useHookLoader: useOnSitePowerGenerationSchema,
    scopeCategory: questionScopeCategories.SCOPE_1,
    isCompletePropName: 'isOnSitePowerGenerationComplete',
    apiKey: 'calculation-on-site-power-generation',
    title: i18n.t('business:Questions.calculations.onSitePowerGeneration.title'),
    primaryI18nKey: 'business:QuestionPage.forms.onSitePowerGeneration.key', // question selection step title
    subTextI18nKey: 'business:QuestionPage.forms.onSitePowerGeneration.tip', // question selection step subtitle
    overviewPageTitleI18nKey: 'business:QuestionPage.forms.onSitePowerGeneration.key',
    icon: gasCannister,
    initiative: {
      getVisuals: ({ frontendEnum, questionTypeKey = null, isBpOffering }) => {
        if (!isBpOffering) {
          return {
            description: <>test</>,
            background: initiativeBackground,
            useMask: false,
            cardBackground: initiativeCardBackground,
          };
        }
        if (isBpOffering) {
          const { cardImage, headerImage } = initiativeVisuals(frontendEnum, questionTypeKey);
          return {
            description: <>test1</>,
            background: headerImage,
            useMask: false,
            cardBackground: cardImage,
          };
        }
      },
      formatForInitiativeSummary: ({ initiativeAnswerEnum = null } = {}) => {
        return {
          isShowingReportingPeriod: false,
          isShowingReportingPeriodNoReduction: true,
          category: initiativeAnswerEnum,
          description: `Remaining ${initiativeAnswerEnum} consumption for fuels burned on-site to reduce after incorporating the initiatives already added to the plan so far`,
          descriptionNoReduction: `${initiativeAnswerEnum} consumption for fuels burned on-site during the reporting period `,
        };
      },
    },
    reductionPageKey: 'onSitePowerGeneration',
    reductionPageChartTitle: i18n.t('business:QuestionPage.forms.onSitePowerGeneration.key'),
    reductionPageCards: reductionCardsSlides,
    table: {
      title: i18n.t('business:Questions.calculations.onSitePowerGeneration.table.title'),
      calculateTotal: (allItems: BasketTableAnswerItem[]) => allItems.length,
      totalType: basketTableTotalTypes.COUNT,
      headings: [
        i18n.t('business:Questions.calculations.question3.table.heading1'),
        i18n.t('business:Questions.calculations.question3.table.heading2'),
        i18n.t('business:Questions.calculations.question3.table.heading3'),
      ],
      cells: [
        'fuelType',
        ({ quantity, fuelUnit = 'units', harmonisedInjection }: FuelQuantityRow) =>
          `${quantity} ${i18n.t('business:emissionFactors.volumes.'.concat(fuelUnit), fuelUnit)} ${harmonisedInjection(fuelUnit)}`,
        ({ tonnage }: FuelTonnageRow) => `${formatNumber(tonnage)} tCO\u2082e`,
      ],
      isAnswerEditable: false,
      supplier: {
        columns: ['emissionSource', 'supplierName', 'scope1orAnd3Intensity', 'quantity', 'carbonEmissions'],
      },
      custom: {
        columns: ['emissionSource', 'intensityValue', 'unitOfMeasurement', 'quantity', 'carbonEmissions'],
      },
    },
    details: {
      title: 'fuelType',
      items: [
        'fuelType',
        ({ quantity, fuelUnit = 'units' }: Omit<FuelQuantityRow, 'harmonisedInjection'>) =>
          `${quantity} ${i18n.t('business:emissionFactors.volumes.'.concat(fuelUnit), fuelUnit)}`,
        ({ tonnage }: FuelTonnageRow) => `${formatNumber(tonnage)} ${i18n.t('business:Snippets.tonnes')}`,
        {
          value: 'dataSource',
          title: i18n.t('business:Questions.calculations.question3.details.title'),
        },
      ],
    },
    selector: {
      injectQuestionBranchSelector: true,
      questionBranchEnableCustomData: true,
      questionBranchEnableSupplierData: true,
      para: 'To calculate emissions associated with fuels used for on-site power generation by your business, you can use a combination of activity data, supplier data, and custom data.',
      options: [
        {
          title: questionSelectorOptions.ACTIVITY,
        },
        {
          title: questionSelectorOptions.SUPPLIER,
        },
        {
          title: questionSelectorOptions.CUSTOM,
        },
      ],
    },
    education: {
      hasEducationWidget: true,
      educationContent: getEducationContent(),
    },
    useQuestionBranchSupplierLoader: () =>
      useFormatSharedSupplierScope1({
        questionFormatName: 'on-site-power-generation',
        emissionSourcePlaceholder: 'Select type of fuel',
      }),
    useQuestionBranchCustomLoader: () =>
      useFormatSharedCustomScope1({ questionFormatName: 'on-site-power-generation' }),
    supplierAndCustomSubheader: (
      <>
        <StyledParagraph $largeText $hasExtraMarginBottom>
          Enter data about fuels that have been used for on-site power generation by your business during the reporting
          period: <ReportingPeriod $green />
        </StyledParagraph>
        <StyledInfoFlash>
          <div>For more information, refer to the "Help" section for detailed guidance.</div>
        </StyledInfoFlash>
      </>
    ),
    activitySubheader: (
      <>
        <StyledParagraph $largeText $hasExtraMarginBottom>
          What fuels have been consumed for on-site power generation by your business during the reporting period:{' '}
          <ReportingPeriod $green />
        </StyledParagraph>
        <StyledInfoFlash>
          <div>For more information, refer to the "Help" section for detailed guidance.</div>
        </StyledInfoFlash>
      </>
    ),
  };
};
